import React from "react";

import { Box, useTheme, Theme, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { last } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import { PastLogFragment } from "@/gql/graphql";
import { truncate } from "@/utils/number";

import { getProgressValue } from "../utils/getProgressValue";

const useStyles = makeStyles()((theme: Theme) => ({
  goalWeight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 1),
  },
  colorPrimary: {
    color: theme.palette.primary.main,
  },
  progress: {
    width: "100%",
  },
}));

const StyledLinearProgress = withStyles(LinearProgress, (theme: Theme) => ({
  root: {
    borderRadius: "24px",
  },
  colorPrimary: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 700],
  },
}));

interface Props {
  currentWeight: number;
  targetWeight: number | null | undefined;
  logs: PastLogFragment[];
}

const GoalProgress: React.FC<Props> = ({
  currentWeight,
  targetWeight,
  logs,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const initialWeight = last(logs)?.value ?? 0;
  const progressValue = getProgressValue(
    currentWeight,
    initialWeight,
    targetWeight ?? 0
  );
  return (
    <>
      <div className={classes.goalWeight}>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="subtitle1" color="textSecondary">
            Initial Weight
          </Typography>
          <Box marginRight={theme.spacing(0.5)} />
          <Typography variant="body1" fontWeight="bold">
            {initialWeight ? `${initialWeight} kg` : "N/A"}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="subtitle1" color="primary">
            Goal Weight
          </Typography>
          <Box marginRight={theme.spacing(0.5)} />
          <Typography
            variant="body1"
            fontWeight="bold"
            className={` ${classes.colorPrimary}`}
          >
            {targetWeight ? `${truncate(targetWeight, 2)} kg` : "N/A"}
          </Typography>
        </Box>
      </div>
      {/* Progress indicator */}
      <div className={classes.progress}>
        <StyledLinearProgress variant="determinate" value={progressValue} />
      </div>
    </>
  );
};

export default GoalProgress;
